import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AppLink, useAppNavigate } from "../lib/route-primitives";
import {
  ArrowLeft, AlertTriangle, CheckCircle2, RefreshCw, Trash2,
  ChevronDown, ChevronRight, Loader2, Search, ShieldAlert, Zap, Clock, Activity,
} from "lucide-react";
import { adminMaintenanceApi, type KvMetricsData } from "../lib/api";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";

// ─── Types ────────────────────────────────────────────────────

interface ErrorGroup {
  fingerprint: string; norm_path: string; method: string;
  error_kind: "exception" | "soft"; error_type: string | null;
  message: string; status_code: number; count: number;
  first_seen: string; last_seen: string; is_resolved: number;
  resolved_at: string | null; resolution_note: string | null;
}
interface ErrorEvent {
  id: string; fingerprint: string; request_id: string;
  method: string; path: string; norm_path: string;
  status_code: number; error_kind: string; error_type: string | null;
  message: string; stack: string | null; metadata: string | null;
  actor_type: string | null; actor_id: string | null;
  wedding_slug: string | null; created_at: string;
}
interface MaintenanceStats {
  totalGroups: number; unresolved: number; last24h: number; last7d: number;
  topPaths: { norm_path: string; total_count: number; last_seen: string }[];
}

// ─── Helpers ──────────────────────────────────────────────────

function relativeTime(ts: string, t: (k: string, o?: Record<string, number>) => string) {
  const diff = Date.now() - new Date(ts.replace(" ", "T") + "Z").getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("maintenance.timeJustNow");
  if (min < 60) return t("maintenance.timeMinutes", { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("maintenance.timeHours", { count: h });
  return t("maintenance.timeDays", { count: Math.floor(h / 24) });
}
function fullDate(ts: string, lang: string) {
  return new Date(ts.replace(" ", "T") + "Z").toLocaleString(lang);
}

// ─── Badges ───────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  session:   "bg-blue-500/10 text-blue-400 border-blue-500/20",
  ratelimit: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  otp:       "bg-violet-500/10 text-violet-400 border-violet-500/20",
  upload:    "bg-sky-500/10 text-sky-400 border-sky-500/20",
  config:    "bg-slate-500/10 text-slate-400 border-slate-500/20",
};

const METHOD_CLS: Record<string, string> = {
  GET:    "bg-blue-500/10 text-blue-400 border-blue-500/20",
  POST:   "bg-green-500/10 text-green-400 border-green-500/20",
  PATCH:  "bg-amber-500/10 text-amber-400 border-amber-500/20",
  PUT:    "bg-amber-500/10 text-amber-400 border-amber-500/20",
  DELETE: "bg-red-500/10 text-red-400 border-red-500/20",
};

function MethodBadge({ method }: { method: string }) {
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold font-mono border",
      METHOD_CLS[method] ?? "bg-muted text-muted-foreground border-border")}>
      {method}
    </span>
  );
}
function KindBadge({ kind }: { kind: string }) {
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border",
      kind === "exception"
        ? "bg-red-500/10 text-red-400 border-red-500/20"
        : "bg-amber-500/10 text-amber-400 border-amber-500/20")}>
      {kind}
    </span>
  );
}
function StatusCode({ code }: { code: number }) {
  const cls = code >= 500 ? "text-red-400 font-bold"
    : code >= 400 ? "text-amber-400 font-semibold"
    : "text-muted-foreground";
  return <span className={cn("font-mono text-xs", cls)}>{code || "—"}</span>;
}

// ─── Stat Card ────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, danger, warn }: {
  label: string; value: number; icon: React.ElementType; danger?: boolean; warn?: boolean;
}) {
  const big = danger && value > 0 ? "text-red-400"
    : warn && value > 0 ? "text-amber-400"
    : "text-foreground";
  const iconCls = danger && value > 0 ? "text-red-400 bg-red-500/10"
    : warn && value > 0 ? "text-amber-400 bg-amber-500/10"
    : "text-primary/60 bg-primary/10";

  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
      <div className={cn("p-2 rounded-xl shrink-0 mt-0.5", iconCls)}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className={cn("text-2xl font-black leading-none", big)}>{value.toLocaleString()}</p>
      </div>
    </div>
  );
}

// ─── KVMetricsTab ─────────────────────────────────────────────

function KVMetricsTab({ window: w, refreshKey }: { window: "1h" | "24h" | "7d"; refreshKey: number }) {
  const [data, setData]       = useState<KvMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async (win: "1h" | "24h" | "7d") => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminMaintenanceApi.getKvMetrics(win);
      setData((res.data as KvMetricsData) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hata");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(w); }, [load, w, refreshKey]);

  const total = useMemo(() => data?.breakdown.reduce((s, r) => s + r.n, 0) ?? 0, [data]);

  if (loading) return (
    <div className="space-y-4">
      {[0, 1, 2].map(i => <div key={i} className="rounded-2xl border border-border h-40 bg-muted animate-pulse" />)}
    </div>
  );

  if (error === "not_configured") return (
    <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
      <p className="font-semibold mb-1">Yapılandırılmamış</p>
      <p>CF_API_TOKEN secret ayarlanmamış.</p>
      <code className="mt-2 block text-xs bg-muted/50 rounded px-3 py-1.5">wrangler secret put CF_API_TOKEN</code>
    </div>
  );

  if (error) return (
    <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-6 text-sm text-red-400 flex items-center justify-between gap-4">
      <span>{error}</span>
      <button onClick={() => load(w)}
        className="shrink-0 px-3 py-1.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 transition-colors cursor-pointer">
        Yeniden dene
      </button>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Breakdown */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          KV Op Breakdown
        </div>
        {!data?.breakdown.length ? (
          <p className="px-5 py-8 text-sm text-center text-muted-foreground">Henüz veri yok</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/40 bg-muted/20 text-xs text-muted-foreground">
                <th className="px-5 py-2.5 text-left">Category</th>
                <th className="px-4 py-2.5 text-left">Op</th>
                <th className="px-4 py-2.5 text-right">Count</th>
                <th className="px-4 py-2.5 text-right hidden sm:table-cell">Avg ms</th>
                <th className="px-5 py-2.5 w-28 hidden sm:table-cell" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {data.breakdown.map((row, i) => (
                <tr key={i} className="hover:bg-primary/5 transition-colors">
                  <td className="px-5 py-3">
                    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border",
                      CATEGORY_COLORS[row.category] ?? "bg-muted text-muted-foreground border-border")}>
                      {row.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.op}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{row.n.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground tabular-nums hidden sm:table-cell">{row.avg_ms}</td>
                  <td className="px-5 py-3 hidden sm:table-cell">
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary/40 transition-all"
                        style={{ width: `${total ? Math.round(row.n / total * 100) : 0}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top IPs */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Top IPs — Rate Limit
        </div>
        {!data?.topIps.length ? (
          <p className="px-5 py-8 text-sm text-center text-muted-foreground">Şüpheli aktivite yok</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/40 bg-muted/20 text-xs text-muted-foreground">
                <th className="px-5 py-2.5 text-left">IP</th>
                <th className="px-4 py-2.5 text-left hidden sm:table-cell">Endpoint</th>
                <th className="px-5 py-2.5 text-right">Hits</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {data.topIps.map((row, i) => (
                <tr key={i} className={cn("transition-colors",
                  row.hits >= 50 ? "bg-red-500/5 hover:bg-red-500/10" : "hover:bg-primary/5")}>
                  <td className={cn("px-5 py-3 font-mono text-xs",
                    row.hits >= 50 ? "text-red-400 font-semibold" : "")}>
                    {row.ip}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden sm:table-cell">{row.sub}</td>
                  <td className={cn("px-5 py-3 text-right font-bold tabular-nums",
                    row.hits >= 50 ? "text-red-400" : "")}>
                    {row.hits.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top Routes */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Top Routes — KV Puts
        </div>
        {!data?.topRoutes.length ? (
          <p className="px-5 py-8 text-sm text-center text-muted-foreground">Henüz veri yok</p>
        ) : (() => {
          const routeTotal = data.topRoutes.reduce((s, r) => s + r.n, 0);
          return (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/40 bg-muted/20 text-xs text-muted-foreground">
                  <th className="px-5 py-2.5 text-left">Route</th>
                  <th className="px-4 py-2.5 text-right">Puts</th>
                  <th className="px-5 py-2.5 w-28 hidden sm:table-cell" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {data.topRoutes.map((row, i) => (
                  <tr key={i} className="hover:bg-primary/5 transition-colors">
                    <td className="px-5 py-3 font-mono text-xs">{row.route || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">{row.n.toLocaleString()}</td>
                    <td className="px-5 py-3 hidden sm:table-cell">
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary/40 transition-all"
                          style={{ width: `${routeTotal ? Math.round(row.n / routeTotal * 100) : 0}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </div>

    </div>
  );
}

// ─── MaintenancePage ──────────────────────────────────────────

const PAGE_SIZE = 50;

export function MaintenancePage() {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();

  const [stats, setStats]     = useState<MaintenanceStats | null>(null);
  const [groups, setGroups]   = useState<ErrorGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [purging, setPurging]         = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"errors" | "kv">("errors");
  const [kvWindow, setKvWindow]   = useState<"1h" | "24h" | "7d">("24h");
  const [kvRefreshKey, setKvRefreshKey] = useState(0);

  const [filterKind, setFilterKind]         = useState<"all" | "exception" | "soft">("all");
  const [filterResolved, setFilterResolved] = useState<"0" | "all">("0");
  const [pathSearch, setPathSearch]         = useState("");

  const buildParams = useCallback((cursor?: string) => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (filterKind !== "all") p.set("kind", filterKind);
    if (filterResolved !== "all") p.set("resolved", filterResolved);
    if (pathSearch) p.set("path_prefix", pathSearch);
    if (cursor) p.set("cursor", cursor);
    return p;
  }, [filterKind, filterResolved, pathSearch]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, groupsRes] = await Promise.all([
        adminMaintenanceApi.getStats(),
        adminMaintenanceApi.getGroups(buildParams()),
      ]);
      setStats((statsRes.data as MaintenanceStats) ?? null);
      const rows = (groupsRes.data as ErrorGroup[] | undefined) ?? [];
      setGroups(rows);
      setHasMore(rows.length >= PAGE_SIZE);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [buildParams]);

  const fetchMore = async () => {
    if (!groups.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await adminMaintenanceApi.getGroups(buildParams(groups[groups.length - 1].last_seen));
      const rows = (res.data as ErrorGroup[] | undefined) ?? [];
      setGroups((p) => [...p, ...rows]);
      setHasMore(rows.length >= PAGE_SIZE);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  const handlePurge = async () => {
    setPurging(true);
    setShowPurgeModal(false);
    try { await adminMaintenanceApi.purge(); await fetchAll(); }
    catch { /* ignore */ }
    finally { setPurging(false); }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const processedGroups = useMemo(
    () => groups.map((g) => ({ ...g, _rel: relativeTime(g.last_seen, t) })),
    [groups, t]
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif italic text-3xl text-foreground">{t("maintenance.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("maintenance.description")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => activeTab === "kv" ? setKvRefreshKey(k => k + 1) : fetchAll()}
            className="p-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-all cursor-pointer">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={() => setShowPurgeModal(true)} disabled={purging}
            title={t("maintenance.purgeDetail")}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-destructive hover:border-destructive/30 hover:bg-destructive/5 transition-all cursor-pointer disabled:opacity-50">
            {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {t("maintenance.purge")}
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-1 p-1 rounded-xl bg-muted/40 border border-border/60">
          {(["errors", "kv"] as const).map(tab => (
            <button key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer",
                activeTab === tab
                  ? "bg-card text-foreground shadow-sm border border-border/60"
                  : "text-muted-foreground hover:text-foreground"
              )}>
              {tab === "errors" ? "Hatalar" : "KV Metrics"}
            </button>
          ))}
        </div>
        {activeTab === "kv" && (
          <div className="flex gap-1">
            {(["1h", "24h", "7d"] as const).map(w => (
              <button key={w}
                onClick={() => setKvWindow(w)}
                className={cn(
                  "px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer",
                  kvWindow === w
                    ? "bg-primary text-white"
                    : "border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                )}>
                {w === "7d" ? "7g" : w}
              </button>
            ))}
          </div>
        )}
      </div>

      {activeTab === "kv" && <KVMetricsTab window={kvWindow} refreshKey={kvRefreshKey} />}

      {activeTab === "errors" && <>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label={t("maintenance.totalGroups")} icon={Activity} value={stats.totalGroups} />
          <StatCard label={t("maintenance.unresolved")} icon={ShieldAlert} value={stats.unresolved} danger />
          <StatCard label={t("maintenance.last24h")} icon={Zap} value={stats.last24h} warn />
          <StatCard label={t("maintenance.last7d")} icon={Clock} value={stats.last7d} />
        </div>
      )}

      {/* Top routes */}
      {stats && stats.topPaths.length > 0 && (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("maintenance.topRoutes")}</p>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              {stats.topPaths.map((p, i) => (
                <tr key={p.norm_path} className="hover:bg-primary/5 transition-colors">
                  <td className="px-5 py-2.5 w-8 text-xs text-muted-foreground font-mono">{i + 1}</td>
                  <td className="px-5 py-2.5 font-mono text-xs text-foreground">{p.norm_path}</td>
                  <td className="px-5 py-2.5 text-right">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-xs font-semibold text-red-400">
                      {p.total_count.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right text-xs text-muted-foreground whitespace-nowrap">
                    {relativeTime(p.last_seen, t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Kind pills */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden">
          {(["all", "exception", "soft"] as const).map((k) => (
            <button key={k} onClick={() => setFilterKind(k)}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                filterKind === k ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}>
              {k === "all" ? t("maintenance.filters.all") : t(`maintenance.kind.${k}`)}
            </button>
          ))}
        </div>

        {/* Resolved toggle */}
        <button onClick={() => setFilterResolved(filterResolved === "0" ? "all" : "0")}
          className={cn("px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer",
            filterResolved === "0"
              ? "bg-primary text-white border-primary"
              : "border-border text-muted-foreground hover:bg-primary/5 hover:text-foreground")}>
          {t("maintenance.filters.unresolvedOnly")}
        </button>

        {/* Path search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input type="text" placeholder={t("maintenance.filters.pathSearch")} value={pathSearch}
            onChange={(e) => setPathSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchAll()}
            className="pl-9 pr-3 py-1.5 text-sm bg-muted/40 border border-primary/20 rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-[border-color,box-shadow] w-52 placeholder:text-muted-foreground/50" />
        </div>

        {!loading && (
          <span className="text-xs text-muted-foreground ml-auto">
            {groups.length} grup{hasMore ? "+" : ""}
          </span>
        )}
      </div>

      {/* Groups table */}
      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 rounded-2xl border border-border">
          <CheckCircle2 className="h-8 w-8 text-green-500" />
          <p className="text-sm text-muted-foreground">{t("maintenance.noErrors")}</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    {[t("maintenance.colLastSeen"), t("maintenance.colKind"), t("maintenance.colMethod"), t("maintenance.colPath"), t("maintenance.colError"), t("maintenance.colCount"), ""].map((h, i) => (
                      <th key={i} className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {processedGroups.map((g) => (
                    <tr key={g.fingerprint}
                      onClick={() => navigate(paths.maintenanceGroup(g.fingerprint))}
                      className={cn("transition-colors cursor-pointer hover:bg-primary/5",
                        g.is_resolved && "opacity-50")}>
                      <td className="px-5 py-3.5 text-xs text-muted-foreground whitespace-nowrap" title={fullDate(g.last_seen, i18n.language)}>
                        {g._rel}
                      </td>
                      <td className="px-5 py-3.5"><KindBadge kind={g.error_kind} /></td>
                      <td className="px-5 py-3.5"><MethodBadge method={g.method} /></td>
                      <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground max-w-[200px] truncate" title={g.norm_path}>
                        {g.norm_path}
                      </td>
                      <td className="px-5 py-3.5 max-w-[260px]">
                        {g.error_type && <span className="text-xs font-semibold text-foreground">{g.error_type}: </span>}
                        <span className="text-xs text-muted-foreground">{g.message}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                          {g.count.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {g.is_resolved
                          ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                          : <span className="h-2 w-2 rounded-full bg-red-500 inline-block" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button onClick={fetchMore} disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-5 py-2.5 text-sm font-medium hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer">
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("common.loadMore")}
              </button>
            </div>
          )}
        </>
      )}

      </> }

      {/* Purge Confirm Modal */}
      {showPurgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPurgeModal(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl p-6 space-y-4">
            <div className="space-y-1.5">
              <h3 className="text-base font-semibold">{t("maintenance.purge")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("maintenance.purgeConfirm")}
              </p>
              <p className="text-xs text-muted-foreground/70">
                {t("maintenance.purgeDetail")}
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowPurgeModal(false)}
                className="flex-1 rounded-xl border border-border py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handlePurge}
                className="flex-1 rounded-xl bg-destructive py-2.5 text-sm font-semibold text-white hover:bg-destructive/90 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {t("maintenance.purge")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── ErrorGroupDetailPage ─────────────────────────────────────

const EVENTS_PAGE_SIZE = 20;

export function ErrorGroupDetailPage() {
  const { t, i18n } = useTranslation();
  const { fingerprint } = useParams<{ fingerprint: string }>();

  const [group, setGroup]           = useState<ErrorGroup | null | undefined>(undefined);
  const [events, setEvents]         = useState<ErrorEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsLoadingMore, setEventsLoadingMore] = useState(false);
  const [eventsHasMore, setEventsHasMore] = useState(false);
  const [note, setNote]             = useState("");
  const [patching, setPatching]     = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!fingerprint) return;
    adminMaintenanceApi.getGroup(fingerprint)
      .then((res) => setGroup((res.data as ErrorGroup) ?? null))
      .catch(() => setGroup(null));
  }, [fingerprint]);

  const fetchEvents = useCallback(async () => {
    if (!fingerprint) return;
    setEventsLoading(true);
    try {
      const res = await adminMaintenanceApi.getEvents(fingerprint);
      const rows = (res.data as ErrorEvent[] | undefined) ?? [];
      setEvents(rows);
      setEventsHasMore(rows.length >= EVENTS_PAGE_SIZE);
    } catch { /* ignore */ }
    finally { setEventsLoading(false); }
  }, [fingerprint]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const fetchMoreEvents = async () => {
    if (!fingerprint || !events.length || eventsLoadingMore) return;
    setEventsLoadingMore(true);
    try {
      const res = await adminMaintenanceApi.getEvents(fingerprint, events[events.length - 1].created_at);
      const rows = (res.data as ErrorEvent[] | undefined) ?? [];
      setEvents((p) => [...p, ...rows]);
      setEventsHasMore(rows.length >= EVENTS_PAGE_SIZE);
    } catch { /* ignore */ }
    finally { setEventsLoadingMore(false); }
  };

  const handlePatch = async (isResolved: boolean) => {
    if (!fingerprint) return;
    setPatching(true);
    try {
      await adminMaintenanceApi.patchGroup(fingerprint, {
        is_resolved: isResolved,
        resolution_note: isResolved ? note || undefined : undefined,
      });
      const res = await adminMaintenanceApi.getGroup(fingerprint);
      setGroup((res.data as ErrorGroup) ?? null);
      if (isResolved) setNote("");
    } catch { /* ignore */ }
    finally { setPatching(false); }
  };

  const toggleEvent = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (group === undefined) return (
    <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
  );
  if (group === null) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <p className="text-muted-foreground text-sm">{t("maintenance.groupNotFound")}</p>
      <AppLink to={paths.maintenance} className="text-sm text-primary hover:underline flex items-center gap-1">
        <ArrowLeft className="h-3.5 w-3.5" /> {t("maintenance.title")}
      </AppLink>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Breadcrumb */}
      <AppLink to={paths.maintenance}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> {t("maintenance.title")}
      </AppLink>

      {/* Header card */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-2 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <KindBadge kind={group.error_kind} />
              <MethodBadge method={group.method} />
              <StatusCode code={group.status_code} />
              {group.is_resolved ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20 text-[10px] font-bold uppercase text-green-400">
                  <CheckCircle2 className="h-3 w-3" /> {t("maintenance.statusResolved")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-[10px] font-bold uppercase text-red-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> {t("maintenance.statusOpen")}
                </span>
              )}
            </div>
            <p className="font-mono text-sm font-semibold text-foreground break-all">{group.norm_path}</p>
            <p className="text-sm text-muted-foreground">
              {group.error_type && <span className="font-semibold text-foreground">{group.error_type}: </span>}
              {group.message}
            </p>
          </div>

          {/* Resolve action */}
          <div className="shrink-0">
            {group.is_resolved ? (
              <button onClick={() => handlePatch(false)} disabled={patching}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all cursor-pointer disabled:opacity-50">
                {patching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {t("maintenance.markUnresolved")}
              </button>
            ) : (
              <div className="flex flex-col gap-2 items-end">
                <input type="text" placeholder={t("maintenance.resolutionNote")} value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg px-3 py-1.5 text-sm w-52 transition-[border-color,box-shadow]" />
                <button onClick={() => handlePatch(true)} disabled={patching}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 cursor-pointer shadow-sm">
                  {patching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  {t("maintenance.markResolved")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-border">
          {[
            { label: t("maintenance.totalCount"), value: group.count.toLocaleString() },
            { label: t("maintenance.firstSeen"), value: relativeTime(group.first_seen, t), title: fullDate(group.first_seen, i18n.language) },
            { label: t("maintenance.lastSeen"), value: relativeTime(group.last_seen, t), title: fullDate(group.last_seen, i18n.language) },
            { label: "Fingerprint", value: group.fingerprint.slice(0, 10) + "…" },
          ].map((item) => (
            <div key={item.label} title={item.title}>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{item.label}</p>
              <p className="text-sm font-semibold font-mono">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Resolution note */}
        {group.is_resolved && group.resolution_note && (
          <div className="flex items-start gap-2 rounded-xl bg-green-500/5 border border-green-500/20 px-4 py-3">
            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-green-500">{t("maintenance.resolutionNote")}: </span>
              {group.resolution_note}
            </p>
          </div>
        )}
      </div>

      {/* Events */}
      <div className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("maintenance.events")}
          {events.length > 0 && <span className="ml-2 font-normal normal-case tracking-normal">({events.length}{eventsHasMore ? "+" : ""})</span>}
        </h2>

        {eventsLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("maintenance.noErrors")}</p>
        ) : (
          <>
            <div className="rounded-2xl border border-border overflow-hidden divide-y divide-border">
              {events.map((evt) => (
                <div key={evt.id}>
                  <button
                    onClick={() => toggleEvent(evt.id)}
                    className="w-full flex items-center justify-between px-5 py-3 text-sm hover:bg-primary/5 transition-colors text-left cursor-pointer">
                    <div className="flex items-center gap-3 min-w-0">
                      {expandedIds.has(evt.id)
                        ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      <span className="text-xs text-muted-foreground whitespace-nowrap" title={fullDate(evt.created_at, i18n.language)}>
                        {relativeTime(evt.created_at, t)}
                      </span>
                      <MethodBadge method={evt.method} />
                      <span className="font-mono text-xs text-muted-foreground truncate">{evt.path}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <StatusCode code={evt.status_code} />
                      {evt.actor_type && (
                        <span className="text-[10px] text-muted-foreground font-mono">{evt.actor_type}</span>
                      )}
                    </div>
                  </button>

                  {expandedIds.has(evt.id) && (
                    <div className="px-5 py-4 bg-muted/20 space-y-3 border-t border-border">
                      {/* Meta */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {[
                          { label: t("maintenance.requestId"), value: evt.request_id?.slice(0, 12) + "…" },
                          { label: t("maintenance.actorLabel"), value: evt.actor_type ?? "—" },
                          { label: t("maintenance.weddingLabel"), value: evt.wedding_slug ?? "—" },
                          { label: t("maintenance.dateLabel"), value: fullDate(evt.created_at, i18n.language) },
                        ].map((m) => (
                          <div key={m.label}>
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">{m.label}</p>
                            <p className="font-mono text-xs text-foreground">{m.value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Message */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{t("maintenance.message")}</p>
                        <p className="text-xs text-foreground font-mono">
                          {evt.error_type && <span className="font-bold">{evt.error_type}: </span>}
                          {evt.message}
                        </p>
                      </div>

                      {/* Stack */}
                      {evt.stack && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{t("maintenance.stackTrace")}</p>
                          <pre className="text-xs font-mono text-foreground/70 whitespace-pre-wrap break-all max-h-48 overflow-auto bg-background/60 rounded-xl p-3 border border-border">
                            {evt.stack}
                          </pre>
                        </div>
                      )}

                      {/* Metadata */}
                      {evt.metadata && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Metadata</p>
                          <pre className="text-xs font-mono text-foreground/70 whitespace-pre-wrap break-all max-h-40 overflow-auto bg-background/60 rounded-xl p-3 border border-border">
                            {(() => { try { return JSON.stringify(JSON.parse(evt.metadata), null, 2); } catch { return evt.metadata; } })()}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {eventsHasMore && (
              <div className="flex justify-center">
                <button onClick={fetchMoreEvents} disabled={eventsLoadingMore}
                  className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-5 py-2.5 text-sm font-medium hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer">
                  {eventsLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("common.loadMore")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
